import React, { useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Paper,
  useTheme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChatIcon from '@mui/icons-material/Chat';
import HelpIcon from '@mui/icons-material/Help';
import ArticleIcon from '@mui/icons-material/Article';

declare global {
  interface Window {
    zE: any;
    zESettings: any;
    $zopim: any;
  }
}

const SupportPage: React.FC = () => {
  const theme = useTheme();

  useEffect(() => {
    // Check if we need to show the launcher
    showZendeskLauncherIfNeeded();
  }, []);

  // Function to show Zendesk launcher if user has previously engaged
  const showZendeskLauncherIfNeeded = () => {
    const hasEngagedWithSupport = localStorage.getItem('hasEngagedWithSupport') === 'true';
    
    if (hasEngagedWithSupport) {
      // Show the launcher after a short delay to ensure Zendesk is loaded
      setTimeout(() => {
        const zendeskLauncher = document.querySelector('div[data-testid="launcher"]') as HTMLElement;
        if (zendeskLauncher) {
          zendeskLauncher.style.display = 'block';
        }
      }, 1000);
    }
  };

  // Function to open Zendesk widget
  const openZendeskChat = () => {
    if (window.zE) {
      // Mark that user has engaged with support
      localStorage.setItem('hasEngagedWithSupport', 'true');
      
      // Show the launcher for future visits
      const zendeskLauncher = document.querySelector('div[data-testid="launcher"]') as HTMLElement;
      if (zendeskLauncher) {
        zendeskLauncher.style.display = 'block';
      }
      
      // Using new Web SDK API
      window.zE('messenger', 'open');
    }
  };

  // Support action cards
  const supportActions = [
    {
      title: 'Speak to Agent',
      description: 'Get immediate help from our support team',
      icon: <ChatIcon sx={{ fontSize: 60, color: theme.palette.primary.main }} />,
      action: openZendeskChat
    },
    {
      title: 'Get Quick Answer',
      description: 'Find solutions to common questions',
      icon: <HelpIcon sx={{ fontSize: 60, color: theme.palette.primary.main }} />,
      action: () => openZendeskChat() // This could be different in the future
    },
    {
      title: 'Support Articles FAQ',
      description: 'Browse our knowledge base of helpful articles',
      icon: <ArticleIcon sx={{ fontSize: 60, color: theme.palette.primary.main }} />,
      action: () => openZendeskChat() // This could be different in the future
    },
  ];

  // FAQ items
  const faqItems = [
    {
      question: 'How do I create a new order?',
      answer: 'To create a new order, navigate to the "Orders" section and click on the "Create Order" button. Fill in the required details about your task, and submit the form.'
    },
    {
      question: 'How do I message a handyman?',
      answer: 'You can message a handyman by navigating to their profile and clicking on the "Message" button. Alternatively, you can go to the "Chats" section to see all your ongoing conversations.'
    },
    {
      question: 'How do I leave a review?',
      answer: 'After a job has been completed, you can leave a review by going to the order details page and clicking on the "Leave Review" button. Rate your experience and provide feedback.'
    },
    {
      question: 'How do I update my profile information?',
      answer: 'To update your profile information, go to the "Profile" section and click on "Edit Profile". Make the necessary changes and save them.'
    },
    {
      question: 'What payment methods are accepted?',
      answer: 'We accept various payment methods including credit cards, debit cards, and digital wallets. You can add or manage your payment methods in the "Payment" section of your profile.'
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      {/* Page Title */}
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4, color: theme.palette.text.primary, fontWeight: 500 }}>
        Help Center
      </Typography>

      {/* First Section: Support Actions */}
      <Paper 
        elevation={1} 
        sx={{ 
          p: 3, 
          mb: 4, 
          borderRadius: '8px',
          border: '1px solid',
          borderColor: theme.palette.divider,
          bgcolor: theme.palette.background.paper
        }}
      >
        <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
          How can we help you today?
        </Typography>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {supportActions.map((action, index) => (
            <Grid item xs={12} sm={4} key={index}>
              <Card sx={{ 
                height: '100%',
                borderRadius: '8px',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[4]
                }
              }}>
                <CardActionArea onClick={action.action} sx={{ height: '100%', p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2, pb: 1 }}>
                    {action.icon}
                  </Box>
                  <CardContent>
                    <Typography gutterBottom variant="h6" component="div" align="center" sx={{ fontWeight: 500 }}>
                      {action.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center">
                      {action.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Second Section: FAQs */}
      <Paper 
        elevation={1} 
        sx={{ 
          p: 3,
          borderRadius: '8px',
          border: '1px solid',
          borderColor: theme.palette.divider,
          bgcolor: theme.palette.background.paper
        }}
      >
        <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
          Frequently Asked Questions
        </Typography>
        <Box sx={{ mt: 2 }}>
          {faqItems.map((faq, index) => (
            <Accordion 
              key={index} 
              disableGutters
              elevation={0}
              sx={{
                border: index !== faqItems.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                borderTop: index !== 0 ? 'none' : `1px solid ${theme.palette.divider}`,
                borderLeft: `1px solid ${theme.palette.divider}`,
                borderRight: `1px solid ${theme.palette.divider}`,
                '&:before': {
                  display: 'none',
                },
                borderRadius: index === 0 ? '8px 8px 0 0' : index === faqItems.length - 1 ? '0 0 8px 8px' : 0,
                mb: index === faqItems.length - 1 ? 0 : -1,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${index}-content`}
                id={`panel${index}-header`}
                sx={{ 
                  bgcolor: 'background.paper',
                  borderRadius: index === 0 ? '8px 8px 0 0' : index === faqItems.length - 1 ? '0 0 8px 8px' : 0,
                }}
              >
                <Typography fontWeight="medium" sx={{ color: theme.palette.text.primary }}>{faq.question}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: 'background.paper' }}>
                <Typography sx={{ color: theme.palette.text.secondary }}>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Paper>
    </Container>
  );
};

export default SupportPage; 